import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { downloadReport } from 'src/features/clients/client/reportsV2/store/thunks';
import { InitiateReportRequestModel, ReportGeneration } from 'src/features/clients/client/reportsV2/store/types';
import { PagedResult } from 'src/store';
import { MAX_TAGS } from '../constants';
import {
  createBulkReport,
  fetchBulkReportList,
  fetchFilteredClients,
  fetchFilteredReportClients,
  fetchModelList,
  fetchReportingParameters,
  fetchScheduledEventList,
  fetchSecurities,
  getImmediateBulkReportById,
  getReportById,
  initiateReportGeneration,
} from './thunks';
import {
  BulkReportingState,
  BulkReportJob,
  BulkReportList,
  ClientSearchResult,
  FilteredReportClientViewModel,
  GetFilteredClientEntity,
  GetReportParametersViewModel,
  ImmediateBulkReportByIdViewModel,
  ModelItem,
  PreDefinedTag,
  ScheduledEvent,
  SecurityHeld,
  SecurityItem,
  SetPreDefinedTagValue,
  SwapTagRequest,
} from './types';

const initialState: BulkReportingState = {
  clientTypes: [],
  serviceType: '',
  portfolioValueFrom: null,
  portfolioValueTo: null,
  availableCashBalanceFrom: null,
  availableCashBalanceTo: null,
  securityHeld: SecurityHeld.Held.name,
  security: null,
  securities: [],
  model: null,
  models: [],
  afsls: [],
  clients: [],
  practices: [],
  advisers: [],
  groups: [],
  selectedAfsls: [],
  selectedGroups: [],
  selectedPractices: [],
  selectedAdvisers: [],
  clientSearchResults: [],
  importedClients: [],
  selectedGroupRows: [],
  selectedClientRows: [],
  selectedClientIdRows: new Set<number>(),
  paginatedBulkReportResults: {
    pageNumber: 0,
    pageSize: 0,
    results: [],
    totalNumberOfPages: 0,
    totalNumberOfRecords: 0,
  },
  paginatedEventListResults: {
    pageNumber: 0,
    pageSize: 0,
    results: [],
    totalNumberOfPages: 0,
    totalNumberOfRecords: 0,
  },
  selectedClients: [],
  namingTemplate: [],
  allowTerminatedClient: false,
  reportGeneration: {
    isGenerating: false,
    isExpired: false,
    requestId: '',
    downloadFileName: '',
    isDataFromTriumph: false,
    isMergeReport: false,
    key: '',
    clientId: 0,
    clientName: '',
    isGenerated: false,
    mergeReportOrder: [],
    dateRange: '',
    reportName: '',
    status: '',
    statusMessage: '',
  },
  csvUploaded: false,
  reportingParametersList: {
    pageNumber: 0,
    pageSize: 0,
    results: [],
    totalNumberOfPages: 0,
    totalNumberOfRecords: 0,
  },
};

export const reportingSlice = createSlice({
  name: '@@reporting',
  initialState,
  reducers: {
    clearCurrentDraft: (state) => {
      state.currentDraft = undefined;
    },
    resetBulkReporting: (state) => {
      state.advisers = [];
      state.availableCashBalanceFrom = 0;
      state.availableCashBalanceTo = 0;
      state.clientSearchResults = [];
      state.importedClients = [];
      state.clientTypes = [];
      state.clients = [];
      state.selectedClients = [];
      state.currentDraft = undefined;
      state.groups = [];
      state.model = null;
      state.models = [];
      state.namingTemplate = [];
      state.practices = [];
      state.securities = [];
      state.securityHeld = SecurityHeld.Held.name;
      state.selectedGroups = [];
      state.selectedPractices = [];
      state.selectedAfsls = [];
      state.selectedAdvisers = [];
      state.selectedClientRows = [];
      state.selectedGroupRows = [];
      state.selectedClientIdRows = new Set();
      state.selectedStatuses = [];
      state.allowTerminatedClient = false;
      state.csvUploaded = false;
    },
    discardCsvUpload: (state) => {
      state.csvUploaded = false;
    },
    appendClients: (state, action: PayloadAction<ClientSearchResult[]>) => {
      state.csvUploaded = true;
      state.importedClients = action.payload;
      state.selectedAfsls = [];
      state.selectedClients = [];
      state.selectedGroups = [];
    },
    setClientTypesParameter: (state, action: PayloadAction<string[]>) => {
      state.clientTypes = action.payload;
    },
    setServiceTypeParameter: (state, action: PayloadAction<string>) => {
      state.serviceType = action.payload;
    },
    setPortfolioValueFrom: (state, action: PayloadAction<number | null>) => {
      state.portfolioValueFrom = action.payload;
    },
    setPortfolioValueTo: (state, action: PayloadAction<number | null>) => {
      state.portfolioValueTo = action.payload;
    },
    setAvailableCashBalanceFrom: (state, action: PayloadAction<number | null>) => {
      state.availableCashBalanceFrom = action.payload;
    },
    setAvailableCashBalanceTo: (state, action: PayloadAction<number | null>) => {
      state.availableCashBalanceTo = action.payload;
    },
    setSecurityHeld: (state, action: PayloadAction<string>) => {
      state.securityHeld = action.payload;
    },
    setSecurity: (state, action: PayloadAction<SecurityItem>) => {
      state.security = action.payload;
    },
    setModel: (state, action: PayloadAction<ModelItem | null>) => {
      state.model = action.payload;
    },
    setSelectedStatuses: (state, action: PayloadAction<string[]>) => {
      state.selectedStatuses = action.payload;
    },
    setSelectedAfsls: (state, action: PayloadAction<GetFilteredClientEntity[]>) => {
      state.selectedAfsls = action.payload;
    },
    setSelectedGroups: (state, action: PayloadAction<GetFilteredClientEntity[]>) => {
      state.selectedGroups = action.payload;
    },
    setSelectedPractices: (state, action: PayloadAction<GetFilteredClientEntity[]>) => {
      state.selectedPractices = action.payload;
    },
    setSelectedAdvisers: (state, action: PayloadAction<GetFilteredClientEntity[]>) => {
      state.selectedAdvisers = action.payload;
    },
    setSelectedClientRows: (state, action: PayloadAction<number[]>) => {
      state.selectedClientRows = action.payload;
    },
    setSelectedClientIdRows: (state, action: PayloadAction<Set<number>>) => {
      state.selectedClientIdRows = action.payload;
    },
    setSelectedGroupRows: (state, action: PayloadAction<number[]>) => {
      state.selectedGroupRows = action.payload;
    },
    setAllowTerminatedClient: (state, action: PayloadAction<boolean>) => {
      state.allowTerminatedClient = action.payload;
    },
    setSelectedClients: (state, action: PayloadAction<GetFilteredClientEntity[]>) => {
      state.selectedClients = action.payload;
    },
    setNamingTemplateTagValue: (state, action: PayloadAction<SetPreDefinedTagValue>) => {
      state.namingTemplate[action.payload.index].value = action.payload.value;
    },
    setNamingTemplate: (state, action: PayloadAction<PreDefinedTag[]>) => {
      state.namingTemplate = action.payload;
    },
    appendNamingTemplate: (state, action: PayloadAction<PreDefinedTag>) => {
      if (state.namingTemplate.length && state.namingTemplate.length >= MAX_TAGS) {
        return;
      }
      state.namingTemplate.push(action.payload);
    },
    clearNamingTemplate: (state) => {
      state.namingTemplate = [];
    },
    deleteNamingTemplateTag: (state, action: PayloadAction<number>) => {
      state.namingTemplate.splice(action.payload, 1);
    },
    swapNamingTemplateTags: (state, action: PayloadAction<SwapTagRequest>) => {
      const { from, to } = action.payload;
      const fromValue = state.namingTemplate[from];
      state.namingTemplate[from] = state.namingTemplate[to];
      state.namingTemplate[to] = fromValue;
    },
    setReportGeneration: (state, action: PayloadAction<ReportGeneration>) => {
      state.reportGeneration = {
        ...state.reportGeneration,
        isGenerating: action.payload.isGenerating,
        requestId: action.payload.requestId,
        downloadFileName: action.payload.downloadFileName,
        isExpired: action.payload.isExpired,
        isDataFromTriumph: action.payload.isDataFromTriumph,
        isMergeReport: action.payload.isMergeReport,
        key: action.payload.key,
        clientId: action.payload.clientId,
        clientName: action.payload.clientName,
        mergeReportOrder: action.payload.mergeReportOrder,
        dateRange: action.payload.dateRange,
        reportName: action.payload.reportName,
      };
    },
    clearImmediateBulkReport: (state) => {
      state.immediateBulkReport = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFilteredReportClients.fulfilled, (state, action: PayloadAction<FilteredReportClientViewModel>) => {
      state.afsls = action.payload.afsls;
      state.advisers = action.payload.advisers;
      state.clients = action.payload.clients;
      state.groups = action.payload.groups;
      state.practices = action.payload.practices;
    });
    builder.addCase(getImmediateBulkReportById.pending, (state) => {
      state.immediateBulkReport = undefined;
    });
    builder.addCase(getImmediateBulkReportById.fulfilled, (state, action: PayloadAction<ImmediateBulkReportByIdViewModel>) => {
      state.immediateBulkReport = action.payload;
    });
    builder.addCase(fetchSecurities.fulfilled, (state, action: PayloadAction<SecurityItem[]>) => {
      state.securities = action.payload;
    });
    builder.addCase(fetchModelList.fulfilled, (state, action: PayloadAction<ModelItem[]>) => {
      state.models = action.payload;
    });
    builder.addCase(fetchReportingParameters.fulfilled, (state, action: PayloadAction<PagedResult<GetReportParametersViewModel>>) => {
      state.reportingParametersList = action.payload;
    });
    builder.addCase(fetchFilteredClients.fulfilled, (state, action: PayloadAction<ClientSearchResult[]>) => {
      const uniqueClientsMap = new Map<number, ClientSearchResult>();

      action.payload.forEach((client) => {
        uniqueClientsMap.set(client.clientId, client);
      });

      state.clientSearchResults = Array.from(uniqueClientsMap.values());
    });
    builder.addCase(createBulkReport.fulfilled, (state, action: PayloadAction<BulkReportJob>) => {
      state.currentDraft = action.payload;
    });
    builder.addCase(fetchBulkReportList.fulfilled, (state, action: PayloadAction<BulkReportList>) => {
      state.paginatedBulkReportResults = action.payload;
    });
    builder.addCase(fetchScheduledEventList.fulfilled, (state, action: PayloadAction<PagedResult<ScheduledEvent>>) => {
      state.paginatedEventListResults = action.payload;
    });
    builder.addCase(initiateReportGeneration.fulfilled, (state, action: PayloadAction<InitiateReportRequestModel>) => {
      if (action.payload) {
        state.reportGeneration.requestId = action.payload.id;
        state.reportGeneration.isGenerating = true;
        state.reportGeneration.isGenerated = false;
      }
    });
    builder.addCase(initiateReportGeneration.pending, (state) => {
      state.reportGeneration.isGenerating = false;
      state.reportGeneration.isGenerated = false;
    });
    builder.addCase(getReportById.fulfilled, (state, action: PayloadAction<ReportGeneration>) => {
      if (action.payload) {
        state.reportGeneration.isExpired = action.payload.isExpired;
        state.reportGeneration.key = action.payload.key;
        state.reportGeneration.status = action.payload.status;
        state.reportGeneration.statusMessage = action.payload.statusMessage;
        state.reportGeneration.downloadFileName = action.payload.downloadFileName;
        if (action.payload.isExpired || action.payload.status === 'E' || (action.payload.key !== '' && action.payload.key !== null)) {
          state.reportGeneration.isGenerating = false;
        }
      }
    });
    builder.addCase(downloadReport.fulfilled, (state, action: PayloadAction<string>) => {
      if (action.payload) {
        state.reportResult = null;
        state.reportGeneration.key = '';
        state.reportGeneration.isGenerated = true;
        state.reportGeneration.isGenerating = false;
      }
    });
  },
});
